import { useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    Stack,
    Typography,
    Button,
    Divider,
    Modal,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { HotTable } from '@handsontable/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorModal from "src/components/FastReportErrorModal/ErrorModal";
import Handsontable from 'handsontable';
import Services from "src/services/Services";
import { WarningOutlined } from '@mui/icons-material';
import { registerAllModules } from "handsontable/registry";

registerAllModules();

export default function EticaretReport({ parsedUser, language, tabs }) {
    const calculatedHeight = window.innerHeight - 250;
    const initialRows = Array.from({ length: 50 }, (_, index) => ({
        id: index + 1,
        eCommerceBrand: '',
        inBranchDate: '',
        barcode: '',
        prePrintedBarcode: '',
        returnBarcode: '',
        receiverName: '',
        takenByName: '',
        deliveredStaff: '',
        customerDeliveryDate: '',
        processingType: 'Noktada',
    }));

    const [tableData, setTableData] = useState(initialRows);
    const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [failedRowsCount, setFailedRowsCount] = useState(0);
    const [modalType, setModalType] = useState('success');
    const hotRef = useRef(null);

    // ---------- DATE HELPERS ----------
    const toTodayStr = () => {
        const t = new Date();
        const dd = String(t.getDate()).padStart(2, '0');
        const mm = String(t.getMonth() + 1).padStart(2, '0');
        const yyyy = t.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    function parseDDMMYYYY(value) {
        if (!value || typeof value !== 'string') return null;
        const norm = value.replace(/\./g, '/').replace(/-/g, '/');
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(norm);
        if (!m) return null;
        const dd = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const yyyy = parseInt(m[3], 10);
        const d = new Date(yyyy, mm - 1, dd);
        if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
        return d;
    }

    function isFuture(dateObj) {
        if (!(dateObj instanceof Date)) return false;
        const today = new Date();
        today.setHours(0,0,0,0);
        const d = new Date(dateObj);
        d.setHours(0,0,0,0);
        return d.getTime() > today.getTime();
    }

    function convertDateFormat(dateStr) {
        if (!dateStr || typeof dateStr !== 'string') return '';
        const parts = dateStr.split('/');
        if (parts.length !== 3) return dateStr;
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const getColumnHeaders = () => [
        'NO',
        'E-TİCARET MARKASI',
        'KARGO GELİŞ TARİHİ',
        'BARKOD NUMARASI',
        'ALICI MÜŞTERİ ADI',
        'LOKASYON',
        'İŞLEM TÜRÜ',
    ];

    const getColumnSettings = () => {
        const headers = getColumnHeaders();
        const todayDate = new Date();

        return headers.map((header) => {
            if (header === 'NO') {
                return {
                    data: null,
                    readOnly: true,
                    className: 'htCenter htMiddle',
                    renderer: (instance, td, row) => {
                        Handsontable.dom.empty(td);
                        Handsontable.dom.fastInnerHTML(td, row + 1);
                        return td;
                    },
                };
            }

            if (header === 'LOKASYON') {
                return {
                    type: 'text',
                    strict: false,
                    className: 'htCenter htMiddle',
                    placeholder: parsedUser.firstResponsibleOffice.title,
                    readOnly: true,
                };
            }

            if (header === 'E-TİCARET MARKASI') {
                return {
                    type: 'dropdown',
                    strict: true,
                    allowInvalid: false,
                    className: 'htCenter htMiddle',
                    source: ['HEPSİBURADA', 'TRENDYOL', 'N11', 'ARAS KARGO', 'EPX', 'MANUEL'],
                };
            }

            if (header === 'İŞLEM TÜRÜ') {
                return {
                    type: 'text',
                    strict: true,
                    allowInvalid: false,
                    className: 'htCenter htMiddle',
                    defaultValue: 'Noktada',
                    placeholder: 'Noktada',
                    readOnly: true,
                };
            }

            if (['KARGO GELİŞ TARİHİ'].includes(header)) {
                return {
                    type: 'date',
                    dateFormat: 'DD/MM/YYYY',
                    correctFormat: true,
                    allowInvalid: false,            // geçersiz kabul edilmesin
                    strict: true,
                    className: 'htCenter htMiddle',
                    placeholder: 'GG/AA/YYYY',
                    // Pikaday / DatePicker konfig: geleceğe seçim yok
                    datePickerConfig: {
                        firstDay: 1,
                        showWeekNumber: true,
                        maxDate: todayDate,
                        i18n: {
                            previousMonth: 'Previous Month',
                            nextMonth: 'Next Month',
                            months: [
                                'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
                            ],
                            weekdays: [
                                'Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi',
                            ],
                            weekdaysShort: ['Pa', 'Pzt', 'Salı', 'Ça', 'Pe', 'Cu', 'Ct'],
                        },
                    },
                    // Gelecek tarih ve boşluk kontrolü
                    validator: (value, callback) => {
                        const d = parseDDMMYYYY(value);
                        if (!d) return callback(false);
                        if (isFuture(d)) return callback(false);
                        return callback(true);
                    },
                };
            }

            if (['MÜŞTERİYE TESLİM TARİHİ', 'İADE TARİHİ'].includes(header)) {
                return {
                    type: 'date',
                    dateFormat: 'DD/MM/YYYY',
                    correctFormat: true,
                    allowInvalid: true, // bu alanlar zorunlu değil
                    strict: false,
                    className: 'htCenter htMiddle',
                    placeholder: 'GG/AA/YYYY',
                    datePickerConfig: {
                        firstDay: 1,
                        showWeekNumber: true,
                        i18n: {
                            previousMonth: 'Previous Month',
                            nextMonth: 'Next Month',
                            months: [
                                'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
                            ],
                            weekdays: [
                                'Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi',
                            ],
                            weekdaysShort: ['Pa', 'Pzt', 'Salı', 'Ça', 'Pe', 'Cu', 'Ct'],
                        },
                    },
                };
            }

            if (header === 'BARKOD NUMARASI') {
                return { type: 'text', strict: true, allowInvalid: false, className: 'htCenter htMiddle' };
            }

            if (header === 'ALICI MÜŞTERİ ADI') {
                return { type: 'text', strict: true, allowInvalid: false, className: 'htCenter htMiddle' };
            }

            return { type: 'text', strict: false, className: 'htCenter htMiddle' };
        });
    };

    // Otomatik büyük harf + tarih normalize + GELECEK TARİHİ BUGÜNE ÇEK
    const onAfterChange = (changes, source) => {
        if (!hotRef.current || source === 'setDataAtCell') return;
        const hotInstance = hotRef.current.hotInstance;
        const headers = getColumnHeaders();
        const dataToSet = [];

        changes?.forEach(([row, col, prevValue, newValue]) => {
            const header = headers[col];

            // Text alanlarını TR upper
            if (['BARKOD NUMARASI', 'ALICI MÜŞTERİ ADI'].includes(header) && typeof newValue === 'string') {
                const upperValue = newValue.toLocaleUpperCase('tr-TR');
                if (upperValue !== newValue) dataToSet.push([row, col, upperValue]);
            }

            // Tarih normalize
            if (header?.includes('TARİH') && newValue) {
                let formatted = String(newValue).replace(/\./g, '/').replace(/-/g, '/');
                if (!formatted.includes('/') && formatted.length === 8) {
                    formatted = formatted.replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
                }

                // KARGO GELİŞ TARİHİ için gelecek tarih -> bugüne çevir
                if (header === 'KARGO GELİŞ TARİHİ') {
                    const d = parseDDMMYYYY(formatted);
                    if (d && isFuture(d)) {
                        formatted = toTodayStr();
                    }
                }

                dataToSet.push([row, col, formatted]);
            }
        });

        if (dataToSet.length > 0) {
            hotInstance.setDataAtCell(dataToSet, 'setDataAtCell');
        }
    };

    useEffect(() => {
        if (hotRef.current) {
            const hotInstance = hotRef.current.hotInstance;
            hotInstance.loadData(tableData);
            hotInstance.validateCells();
        }
    }, [tableData]);

    useEffect(() => {
        if (isSuccessModalOpen && hotRef.current) {
            setTimeout(() => {
                hotRef.current.hotInstance.validateCells(() => {
                    hotRef.current.hotInstance.render();
                });
            }, 0);
        }
    }, [isSuccessModalOpen]);

    // KARGO GELİŞ TARİHİ kolonunda klavye ile yazmayı engelle
    useEffect(() => {
        if (!hotRef.current) return;
        const hot = hotRef.current.hotInstance;

        const headers = getColumnHeaders();
        const arrivalColIndex = headers.indexOf('KARGO GELİŞ TARİHİ');

        const blockTyping = (e) => {
            const sel = hot.getSelectedLast();
            if (!sel) return;
            const col = sel[1];
            if (col !== arrivalColIndex) return;

            // Karakter girişi, silme vs. hepsini blokla
            const isCharacterOrEdit =
                (e.key && e.key.length === 1) ||
                e.key === 'Backspace' ||
                e.key === 'Delete' ||
                e.key === ' ' ||
                e.key === 'Enter';

            if (isCharacterOrEdit) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };

        hot.addHook('beforeKeyDown', blockTyping);
        return () => {
            try { hot.removeHook('beforeKeyDown', blockTyping); } catch (err) {}
        };
    }, []);

    const handleCreate = () => {
        const hotInstance = hotRef.current.hotInstance;
        const headers = getColumnHeaders();

        hotInstance.validateCells(() => {
            const currentData = hotInstance.getData();
            let validRows = [];
            let errorRows = [];

            const requiredColumns = ['E-TİCARET MARKASI', 'KARGO GELİŞ TARİHİ', 'BARKOD NUMARASI', 'ALICI MÜŞTERİ ADI'];

            currentData.forEach((row, rowIndex) => {
                const rowHasData = row.some((cell, colIndex) => {
                    if (['NO', 'LOKASYON', 'İADE KODU'].includes(headers[colIndex])) return false;
                    return cell && cell.toString().trim() !== '';
                });
                if (!rowHasData) return;

                let rowHasError = false;
                for (let col = 0; col < row.length; col++) {
                    const header = headers[col];

                    if (requiredColumns.includes(header)) {
                        if (!row[col] || row[col].toString().trim() === '') {
                            rowHasError = true;
                            break;
                        }
                    }

                    // Gelecek tarih korumasını bir kez daha uygula
                    if (header === 'KARGO GELİŞ TARİHİ') {
                        const d = parseDDMMYYYY(row[col]);
                        if (!d || isFuture(d)) {
                            // hatayı say ve bugünün tarihi ile değiştir
                            row[col] = toTodayStr();
                        }
                    }

                    const meta = hotInstance.getCellMeta(rowIndex, col);
                    if (meta.valid === false && requiredColumns.includes(header)) {
                        rowHasError = true;
                        break;
                    }
                }

                if (rowHasError) errorRows.push(row);
                else validRows.push(row);
            });

            if (validRows.length === 0) {
                openSuccessModal('İşlenecek geçerli satır yok.', 0, 'error');
                return;
            }

            const totalRows = 50;
            let newData = [...errorRows];
            const headerCount = headers.length;
            while (newData.length < totalRows) {
                let emptyRow = new Array(headerCount).fill('');
                emptyRow[0] = newData.length + 1;
                newData.push(emptyRow);
            }

            setTableData(newData);

            const reportArray = validRows.map((row) => ({
                eCommerceBrand: row[1],
                inBranchDate: convertDateFormat(row[2]),
                barcode: row[3],
                receiverName: row[4],
                processingType: "Noktada",
            }));

            Services.CreateOperationReport(parsedUser.token, reportArray, 'e-commerce')
                .then((response) => {
                    if (response.data.status) {
                        if (errorRows.length === 0) {
                            openSuccessModal('Geçerli satırlar başarıyla işlendi', 0, 'success');
                        } else {
                            openSuccessModal('Geçerli satırlar işlendi. Hatalı satır sayısı', errorRows.length, 'error');
                        }
                    } else {
                        console.log(response.data);
                        openSuccessModal('Bir hata meydana geldi.', 0, 'error');
                    }
                })
                .catch((err) => {
                    console.log(err);
                    openSuccessModal('Servis çağrısı sırasında hata oluştu.', reportArray.length, 'error');
                });
        });
    };

    const openSuccessModal = (message, count, type) => {
        setSuccessMessage(message);
        setFailedRowsCount(count);
        setModalType(type);
        setSuccessModalOpen(true);
    };

    useEffect(() => {
        if (hotRef.current) {
            const hotInstance = hotRef.current.hotInstance;
            hotInstance.addHook('afterChange', onAfterChange);
            hotInstance.updateSettings({
                data: tableData,
                colHeaders: getColumnHeaders(),
                columns: getColumnSettings(),
            });
        }
    }, [tableData]);

    return (
        <div>
            <Stack direction="row" justifyContent='space-between' mb={2}>
                {tabs}
                <Button onClick={handleCreate} variant="contained" endIcon={<SendIcon />}>
                    {language.commons.save}
                </Button>
            </Stack>

            <Card>
                <Scrollbar>
                    <Box sx={{ width: '100%' }}>
                        <HotTable
                            ref={hotRef}
                            rowHeights={35}
                            licenseKey="non-commercial-and-evaluation"
                            data={tableData}
                            persistentState={true}
                            colHeaders={getColumnHeaders()}
                            columns={getColumnSettings()}
                            stretchH="all"
                            height={calculatedHeight}
                        />
                    </Box>
                </Scrollbar>
            </Card>

            <Modal open={isSuccessModalOpen} onClose={() => setSuccessModalOpen(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 4,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                    }}
                >
                    {modalType === 'error' ? (
                        <WarningOutlined sx={{ color: 'orange', fontSize: 64 }} />
                    ) : (
                        <CheckCircleIcon sx={{ color: 'green', fontSize: 64 }} />
                    )}
                    <Typography variant="h6">{successMessage}</Typography>
                    {failedRowsCount > 0 && (
                        <>
                            <Divider sx={{ my: 2, backgroundColor: 'gray' }} />
                            <Typography variant="h6">
                                Uyarı: {failedRowsCount} satırda hata var.
                            </Typography>
                        </>
                    )}
                    <Button
                        onClick={() => {
                            setSuccessModalOpen(false);
                            if (hotRef.current) {
                                setTimeout(() => {
                                    hotRef.current.hotInstance.validateCells(() => {
                                        hotRef.current.hotInstance.render();
                                    });
                                }, 50);
                            }
                        }}
                    >
                        {language.commons.okay}
                    </Button>
                </Box>
            </Modal>

            <ErrorModal />
        </div>
    );
}